import { AppointmentStatusPill } from '@/components/schedule/appointment-status-pill';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Field, SectionCard, StatRow, StatTile } from '@/components/ui/section-card';
import { useProviderAgenda } from '@/hooks/use-appointments';
import { Appointment, APPOINTMENT_STATUS } from '@/types/appointment';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { Enrollments } from '@/types/enrollments';
import { ServiceSchedule } from '@/types/service-schedule';
import {
  dayParts,
  formatMonthLabel,
  formatSlotRange,
  zonedDateKey,
} from '@/utils/format-appointment';
import { Ionicons } from '@expo/vector-icons';
import { differenceInCalendarDays } from 'date-fns';
import { useRouter } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { View } from 'react-native';

/** Janela da agenda lida aqui — igual ao horizonte de materialização. */
const AGENDA_DAYS_AHEAD = 60;
const VISIBLE_SESSIONS = 6;

type EnrollmentServiceTabProps = {
  enrollment: Enrollments | null;
  initialServiceName?: string;
};

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const slotLabel = (schedule: ServiceSchedule) => {
  const weekday = schedule.daysOfWeek?.[0];
  const dayPart =
    weekday != null
      ? WEEKDAYS_SHORT[weekday]
      : schedule.dayOfMonth != null
        ? `dia ${schedule.dayOfMonth}`
        : '';
  const start = schedule.startTime?.slice(0, 5);
  const end = schedule.endTime?.slice(0, 5);
  const timePart = start ? (end ? `${start}–${end}` : start) : '';
  return [dayPart, timePart].filter(Boolean).join(' ');
};

const keyToDate = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const relativeDay = (key: string, todayKey: string) => {
  const diff = differenceInCalendarDays(keyToDate(key), keyToDate(todayKey));
  if (diff <= 0) return 'hoje';
  if (diff === 1) return 'amanhã';
  return `em ${diff} dias`;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Aba de leitura do serviço de um contrato: qual serviço, a cota da assinatura
 * e as próximas sessões com data e horário de fato (não o padrão da grade).
 */
export function EnrollmentServiceTab({ enrollment, initialServiceName }: EnrollmentServiceTabProps) {
  if (!enrollment) return null;

  const service = enrollment.service;

  const quota = enrollment.quotaPerPeriod ?? service?.subscriptionQuota ?? null;
  const quotaPeriod =
    enrollment.quotaPeriod ?? service?.subscriptionQuotaPeriod ?? QUOTA_PERIOD.MONTH;
  const periodWord = quotaPeriod === QUOTA_PERIOD.WEEK ? 'semana' : 'mês';

  const durationMinutes = service?.durationMinutes;
  const slotCapacity = service?.slotCapacity;

  return (
    <View className="w-full gap-5 px-5 pt-5">
      <SectionCard icon="briefcase-outline" title="Serviço">
        <Field label="Serviço" value={service?.name ?? initialServiceName} />
        {service?.description ? <Field label="Descrição" value={service.description} /> : null}
        {durationMinutes || slotCapacity ? (
          <StatRow>
            {durationMinutes ? (
              <StatTile label="Duração" value={`${durationMinutes} min`} />
            ) : null}
            {slotCapacity ? (
              <StatTile
                label="Vagas por horário"
                value={slotCapacity > 1 ? `${slotCapacity} (turma)` : '1 (individual)'}
              />
            ) : null}
          </StatRow>
        ) : null}
      </SectionCard>

      {quota != null ? (
        <SectionCard icon="repeat-outline" title="Assinatura">
          <Field
            label="Cota de sessões"
            value={`${quota} ${quota > 1 ? 'sessões' : 'sessão'} por ${periodWord}`}
            hint="A cota renova a cada período. O cliente marca as sessões dentro dela."
          />
        </SectionCard>
      ) : null}

      <UpcomingSessions
        enrollmentId={enrollment.id}
        schedules={enrollment.serviceSchedules ?? []}
      />
    </View>
  );
}

/**
 * Datas reais do contrato: os agendamentos já materializados na agenda
 * (horizonte rolante de ~60 dias), agrupados por mês. Toque abre o agendamento.
 * Sem nenhum ainda na agenda, cai no resumo da grade.
 */
function UpcomingSessions({
  enrollmentId,
  schedules,
}: {
  enrollmentId: string;
  schedules: ServiceSchedule[];
}) {
  const router = useRouter();
  const [showAll, setShowAll] = useState(false);

  const range = useMemo(() => {
    const now = new Date();
    const to = new Date(now.getTime() + AGENDA_DAYS_AHEAD * 24 * 60 * 60 * 1000);
    return { from: now.toISOString(), to: to.toISOString(), todayKey: zonedDateKey(now.toISOString()) };
  }, []);

  const agenda = useProviderAgenda(range.from, range.to);

  const sessions = useMemo(
    () =>
      (agenda.data ?? [])
        .filter(
          (a) =>
            a.enrollmentId === enrollmentId &&
            a.status !== APPOINTMENT_STATUS.CANCELLED &&
            a.startAt >= range.from,
        )
        .sort((a, b) => a.startAt.localeCompare(b.startAt)),
    [agenda.data, enrollmentId, range.from],
  );

  const visible = showAll ? sessions : sessions.slice(0, VISIBLE_SESSIONS);
  const hidden = sessions.length - visible.length;

  const months = useMemo(() => {
    const groups: { key: string; label: string; items: Appointment[] }[] = [];
    for (const a of visible) {
      const dateKey = zonedDateKey(a.startAt);
      const monthKey = dateKey.slice(0, 7);
      const last = groups[groups.length - 1];
      if (last?.key === monthKey) last.items.push(a);
      else groups.push({ key: monthKey, label: capitalize(formatMonthLabel(dateKey)), items: [a] });
    }
    return groups;
  }, [visible]);

  const badge =
    sessions.length > 0
      ? `${sessions.length} ${sessions.length > 1 ? 'sessões' : 'sessão'}`
      : undefined;

  return (
    <SectionCard icon="calendar-outline" title="Próximas sessões" badge={badge}>
      {agenda.isLoading ? (
        <ThemedText className="text-sm text-neutral-400">Carregando sessões…</ThemedText>
      ) : sessions.length === 0 ? (
        <ThemedText className="text-sm leading-5 text-neutral-400">
          {schedules.length > 0
            ? `Nenhuma sessão na agenda ainda. Grade: ${schedules.map(slotLabel).join(', ')}.`
            : 'Nenhuma sessão marcada. O cliente marca cada sessão quando quiser, dentro da cota.'}
        </ThemedText>
      ) : (
        <View className="gap-4">
          {months.map((month) => (
            <View key={month.key} className="gap-1">
              <ThemedText className="text-xs font-semibold text-neutral-400">
                {month.label}
              </ThemedText>
              {month.items.map((a, index) => {
                const dateKey = zonedDateKey(a.startAt);
                const parts = dayParts(dateKey);
                return (
                  <PressableScale
                    key={a.id}
                    onPress={() => router.push(`/(tabs)/(provider)/schedule/appointment/${a.id}`)}
                    className={`flex-row items-center gap-3 py-2.5 ${
                      index > 0 ? 'border-t border-neutral-100' : ''
                    }`}
                  >
                    <View
                      className="items-center rounded-xl bg-neutral-100 py-1.5"
                      style={{ width: 48, borderCurve: 'continuous' }}
                    >
                      <ThemedText className="text-[11px] font-medium text-neutral-500">
                        {parts.weekday}
                      </ThemedText>
                      <ThemedText className="text-base font-bold text-neutral-900">
                        {parts.day}
                      </ThemedText>
                    </View>

                    <View className="min-w-0 flex-1 gap-0.5">
                      <ThemedText className="text-sm font-semibold text-neutral-900">
                        {formatSlotRange(a.startAt, a.endAt)}
                      </ThemedText>
                      <ThemedText className="text-xs text-neutral-500">
                        {relativeDay(dateKey, range.todayKey)}
                      </ThemedText>
                    </View>

                    {a.status === APPOINTMENT_STATUS.PENDING ? (
                      <AppointmentStatusPill status={a.status} />
                    ) : null}
                    <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
                  </PressableScale>
                );
              })}
            </View>
          ))}

          {hidden > 0 || showAll ? (
            <PressableScale
              onPress={() => setShowAll((v) => !v)}
              className="h-11 items-center justify-center rounded-full border border-neutral-200"
            >
              <ThemedText className="text-sm font-semibold text-neutral-700">
                {showAll ? 'Mostrar menos' : `Mostrar mais ${hidden}`}
              </ThemedText>
            </PressableScale>
          ) : null}
        </View>
      )}
    </SectionCard>
  );
}
